import { type Dispatch, type FC, type SetStateAction, useCallback } from 'react';
import {
    Button,
    ElementHeader,
    type ListBoxOptionItem,
    TagComboboxEditor,
    type TagEditorFactory,
    type TagEditorProps,
    TagGroup,
    type TestProps,
    VStack,
} from '@direct-frontend/components';
import { type ListBoxItemWithScope, type ListBoxOptionItemWithScope, type TagWithScope } from '../../lib/integrations';
import { ErrorBlankSad } from '../ErrorBlank';
import { IntegrationMappingEditorStub } from './IntegrationMappingEditorStub';

export type IntegrationMappingEditorProps = {
    selectedTags: TagWithScope[];
    setSelectedTags: Dispatch<SetStateAction<TagWithScope[]>>;
    availableItems: ListBoxItemWithScope[];
    label?: string;
    isFetching?: boolean;
    isError?: boolean;
    onRetry?: VoidFunction;
    isReadOnly?: boolean;
} & TestProps;

export const IntegrationMappingEditor: FC<IntegrationMappingEditorProps> = ({
    selectedTags,
    setSelectedTags,
    availableItems,
    label,
    isFetching = false,
    isError = false,
    onRetry,
    'data-testid': dataTestId = 'IntegrationMappingEditor',
    isReadOnly = false,
}) => {
    const onAdd = useCallback(
        (item: ListBoxOptionItemWithScope) => {
            setSelectedTags((tags) => tags.concat([{ tag: item.content, key: item.value, scopeArea: item.scopeArea }]));
        },
        [setSelectedTags]
    );

    const onDelete = useCallback(
        (index: number) => {
            const tagsCopy = selectedTags.slice();

            tagsCopy.splice(index, 1);

            setSelectedTags(tagsCopy);
        },
        [selectedTags, setSelectedTags]
    );

    const tagEditorFactory = useCallback<TagEditorFactory<ListBoxOptionItemWithScope>>(
        (props) => (
            <TagComboboxEditor
                {...(props as TagEditorProps<ListBoxOptionItem>)}
                items={availableItems}
                isAddOnBlur={false}
                placeholder={availableItems.length ? 'Введите значение' : ''}
                aria-label="Выбор значений для сопоставления товаров"
                data-testid={`${dataTestId}.Editor`}
            />
        ),
        [availableItems, dataTestId]
    );

    if (isFetching) {
        return <IntegrationMappingEditorStub />;
    }

    if (isError) {
        return (
            <ErrorBlankSad
                title="Произошла ошибка при загрузке характеристик"
                controls={
                    onRetry ? (
                        <Button size="m" color="normal" onClick={onRetry} data-testid={`${dataTestId}.Error.Button`}>
                            Попробовать еще раз
                        </Button>
                    ) : undefined
                }
                data-testid={`${dataTestId}.Error`}
            />
        );
    }

    return (
        <VStack gap="4">
            {label ? (
                <ElementHeader isBold data-testid={`${dataTestId}.Label`}>
                    {label}
                </ElementHeader>
            ) : null}
            <TagGroup<ListBoxOptionItemWithScope>
                tags={selectedTags}
                onAdd={onAdd}
                onDelete={onDelete}
                tagEditorFactory={tagEditorFactory}
                placeholder="Выберите одно или несколько значений"
                data-testid={dataTestId}
                isDisabled={isReadOnly}
            />
        </VStack>
    );
};
